import type { FinanceReadiness } from '../../executionOperations/types';
import type { ExecutionIncident } from '../../routes/execution/incidentFacade';
import {
  getIncidentTypeLabel,
  hasActiveBlockingIncidents
} from '../../routes/execution/incidentFacade';
import type { FinanceBlocker } from '../types';

const REASON_CODES: Record<string, { code: string; severity: 'warning' | 'blocking'; message: string }> = {
  loading_not_complete: {
    code: 'loading_not_complete',
    severity: 'warning',
    message: 'Ожидается погрузка'
  },
  unloading_not_complete: {
    code: 'unloading_not_complete',
    severity: 'warning',
    message: 'Ожидается выгрузка'
  },
  cargo_not_delivered: {
    code: 'cargo_not_delivered',
    severity: 'blocking',
    message: 'Груз не доставлен'
  },
  execution_not_complete: {
    code: 'execution_not_complete',
    severity: 'blocking',
    message: 'Исполнение не завершено'
  },
  documents_pending: {
    code: 'documents_pending',
    severity: 'blocking',
    message: 'Ожидаются документы'
  },
  partial_delivery: {
    code: 'partial_delivery',
    severity: 'blocking',
    message: 'Частичная доставка'
  },
  open_incidents: {
    code: 'open_incidents',
    severity: 'blocking',
    message: 'Есть открытые инциденты'
  },
  downtime_unresolved: {
    code: 'downtime_unresolved',
    severity: 'blocking',
    message: 'Не закрыт простой'
  },
  expenses_unconfirmed: {
    code: 'expenses_unconfirmed',
    severity: 'blocking',
    message: 'Не подтверждены расходы'
  }
};

export function deriveFinanceBlockers(params: {
  readiness: FinanceReadiness | null | undefined;
  incidents?: ExecutionIncident[];
  openDocumentTasks?: number;
  hydrationBlockers?: string[];
}): FinanceBlocker[] {
  const { readiness, incidents = [], openDocumentTasks = 0, hydrationBlockers = [] } = params;
  const blockers: FinanceBlocker[] = [];
  const seen = new Set<string>();

  const push = (b: FinanceBlocker) => {
    if (seen.has(b.code)) return;
    seen.add(b.code);
    blockers.push(b);
  };

  if (hasActiveBlockingIncidents(incidents)) {
    for (const inc of incidents.filter((i) => i.status === 'open' && i.blockingFlag)) {
      push({
        code: `incident_${inc.incidentType}`,
        severity: 'blocking',
        message: getIncidentTypeLabel(inc.incidentType)
      });
    }
  }

  if (incidents.some((i) => i.status === 'open' && i.incidentType === 'downtime')) {
    push(REASON_CODES.downtime_unresolved);
  }

  if (openDocumentTasks > 0) {
    push(REASON_CODES.documents_pending);
  }

  if (hydrationBlockers.includes('partial_delivery')) {
    push(REASON_CODES.partial_delivery);
  }

  for (const reason of readiness?.reasons ?? []) {
    const mapped = REASON_CODES[reason];
    if (mapped) push(mapped);
    else {
      push({ code: reason, severity: 'warning', message: reason });
    }
  }

  return blockers;
}
